/*
 * The Southern Air Import — magazine pagination.
 *
 * Fixed core sequence, with an optional merchant-controlled stretch in the
 * middle (Theme settings → The Southern Air Import → Extra catalog pages):
 *   Page 1        the ad-grid spread (sidebar, search, first 12 items)
 *   Page 2        the Oscar4Fox house ad
 *   Page 3..3+K   extra catalog spreads up through the Mammoth Lakes one
 *                 (K = MAMMOTH_SPREAD_IDX, the 3rd middle spread whenever at
 *                 least 3 exist), nine items each, back-of-book layout
 *   Page 4+K      a second fixed house ad (stained-glass "War is a religion"
 *                 spread) — not merchant-configurable like the partner ad
 *                 slot, always present, clicks straight through to the full
 *                 Manifest. Pulled forward into the middle of the catalog run
 *                 (merchant request) instead of sitting at the back with the
 *                 other two fixed ads, so the issue doesn't front-load all its
 *                 editorial/ad content and tail off into nothing but plain
 *                 spreads — see midSpreadPage()/PAGE_SAT_MID below.
 *   Page 5+K..3+N remaining extra catalog spreads (N = the merchant's
 *                 setting, 0 by default; every one of these page numbers
 *                 shifts up by one from its old flat firstMiddlePage+idx
 *                 value, to make room for the inserted ad above)
 *   Page 4+N      a third fixed house ad (Airwolf vintage-paperback spread) —
 *                 same not-merchant-configurable, always-present treatment as
 *                 the stained-glass ad, clicks straight through to the full
 *                 Manifest. Took over the stained-glass ad's old end-of-issue
 *                 slot when that one moved into the middle.
 *   Page 5+N      the closing back-of-book page — same layout, whatever's left
 *   Page 6+N      the rear cover
 * TOTAL_PAGES is 6 + N and every page-boundary check below reads from it, so
 * turning the extra-pages dial doesn't need any other file to change. There
 * used to be a fixed "page 3" here too — a first back-of-book spread shown
 * right after the Oscar4Fox ad no matter what, with the closing page above
 * being a second, later copy of that exact same layout. Removed: everything
 * past page 1 now comes from one unified pool of leftover items, so the
 * back-of-book shape only ever shows up once per page instead of twice with
 * the same small ads/classifieds/coupon content behind it. The stained-glass
 * house ad is a separate, deliberate second ad placement — distinct from the
 * Oscar4Fox ad, not a duplicate of it — and stays in the issue.
 *
 * With 12 items on page 1 plus 9 per back-of-book page, anything left over in
 * the catalog just isn't printed in this issue — it's still on the full site.
 * That's deliberate: the Import is a curated spread, not a mirror of the
 * whole catalog, and letting it run unbounded was exactly what made the
 * back-of-book pages balloon to the length of the entire product list once
 * stock passed a couple dozen items.
 *
 * Every advertiser gets a slightly different ad shape (span/photo position/type
 * treatment) from a 7-slot cycle keyed off its position in the list, plus small
 * per-item "press imperfections" (skew, ink density, edge fade) computed from the
 * same index — ported as-is from the design source so two different products
 * never render as identical-looking ads next to each other. On top of that, every
 * photo in the Import carries a uniform sepia tint so the whole issue reads as one
 * consistent print run; the per-product detail card you open by clicking an ad is
 * a separate binding (SATComponent's own `detail`, built straight off the product
 * record) and was never wired to this filter, so it always shows the true photo.
 *
 * Home-page teaser: literally page 1 of the real issue, not a separate
 * abbreviated version of it. It used to be its own thing — a stripped-down
 * single column, sidebar hidden, capped to 6 items, sorted its own way —
 * which meant it never actually looked like what opening the Import shows
 * you, and every tweak to the real page 1 (curation, shapes, item count)
 * had to be re-applied to a second, parallel code path to keep them
 * matching. Now `s.page === 'home'` runs through the exact same
 * applyPage1Order + capHeroSpan(..., PAGE1_ITEMS) pipeline as `s.page ===
 * 'store'`'s own page 1, and the three base-renderVals bindings that used
 * to switch the layout to the abbreviated shape for home
 * (saiSidebarShow/saiBodyCols/saiFeaturedShow — see sat-app.js) are
 * overridden below to the same values 'store' gets. The only things still
 * genuinely different on home are the surrounding magazine chrome that
 * wouldn't make sense embedded in a storefront page — no cover flip
 * (saiCoverShow), no prev/next page nav (saiNavShow), no margin page-turn
 * arrows (saiMarginNavShow) — none of that is part of "the ad grid page"
 * itself, and sat-app.js already keeps it off for home on its own.
 *
 * The ad grid is CSS multi-column (column-count:3 — see snippets/sat-import.liquid),
 * not CSS Grid. Two earlier attempts at a 3-column CSS Grid both failed the same
 * way for the same underlying reason: Grid gives every card in a row a SHARED
 * row height (the tallest card's height), and there is no setting that avoids
 * both failure modes at once — align-items:start left a short card's own cell
 * visibly shorter than its row, exposing bare page background where a card
 * should be; align-items:stretch filled that same space, but by force-stretching
 * the short card itself, which just relocated the dead space to INSIDE that
 * card (a big gap between its content and its price/button, and its photo
 * letterboxed into a taller box than its own shape). Multi-column layout has no
 * shared row at all — each card (a normal in-flow block, fit="contain"
 * auto-ratio photo, break-inside:avoid) is exactly as tall as its own content
 * and nothing forces it to match any neighbor, the same way a real magazine's
 * columns run to different natural lengths. columnSpan:'all' on the two hero
 * shapes uses CSS column-span to break a card out full-width, the one thing
 * multi-column can't do per-card the way Grid's span could — real magazines
 * solve this by giving big display ads their own break in the column flow
 * too, so it isn't a compromise, it's the same convention.
 *
 * Merchant: looked at real late-'80s Soldier of Fortune issues (archive.org)
 * for how a real magazine actually handles this. Two findings that matter
 * here: (1) the classified/mail-order pages at the back of a real issue
 * ("Sooper's Locker") pack MUCH denser than this file's old PAGE2_ITEMS=9/11
 * — a dozen-plus small box ads per page, tightly puzzle-packed — and a
 * denser page is exactly what makes multi-column's own balance algorithm
 * work well (see the PAGE1_ITEMS comment below: more, smaller pieces close
 * a column gap tighter than fewer, larger ones can). (2) a real issue
 * carries an actual Advertiser's Index near the back — alphabetical name +
 * page number, real content that's inherently exactly as long as the real
 * page count, never short or long the way a fixed-template grid can be.
 * assets/sat-issue-plan-patch.js's per-page `size` was raised on the
 * single-column spreads to match finding (1); saiIssueIndex below is
 * finding (2), rendered in snippets/sat-import.liquid's Page 2 back matter.
 */
(function () {
  'use strict';
  if (!window.SATComponent) { return; }

  // PAGE1_ITEMS/PAGE2_ITEMS double as (a) the FALLBACK item count when no
  // curated monthly issue is active, and (b) on a curated hasSideStory page
  // (K-219/Vanunu), the live cap on how many of that page's own curated
  // items go to the main grid vs. overflow to the sidebar filler — see
  // `gridCut` in the extraChunks IIFE below. History: started at 12/9, was
  // bumped to 18/11 chasing bare page-background at the bottom of a short
  // column (more, smaller items give multi-column's own balance algorithm
  // finer material to top off a short column with — still true, and still
  // handled by capHeroSpan's medium-first/small-last reorder regardless of
  // count). Cut back down to 12/9 after the merchant reported the resulting
  // pages were simply too long to read without heavy scrolling — a page-fit
  // CSS-zoom experiment (assets/sat-import-page-fit-patch.js) tried to paper
  // over that by shrinking the whole page visually, which just made it
  // illegibly tiny instead of actually shorter; removed (see
  // sections/sat-app.liquid) in favor of this real length cut. The curated
  // issue's own per-page `size` values (assets/sat-issue-plan-patch.js)
  // were cut to match in the same pass.
  var PAGE1_ITEMS = 12;
  var PAGE2_ITEMS = 9;
  // Raised from 6: this was never actually the thing capping how much of the
  // catalog printed — `totalChunks - 1` below already caps extraPages at
  // exactly what the leftover pool needs, so a catalog of any realistic size
  // was already printing in full. Raised anyway as a real ceiling rather than
  // one that happened to not bind yet, so growing the catalog well past its
  // current size can't someday silently drop items off the back of the issue.
  var MAX_EXTRA_PAGES = 40;

  // Belt-and-suspenders: the same item can and should show up on the Import, the
  // Manifest, and a matching Archive terminal — that's the whole point of the
  // cross-listing. What it must never do is show up twice INSIDE one listing (the
  // same product as two different ads on the same page). visibleProducts() isn't
  // expected to hand back duplicates, but this issue is built by slicing one pool
  // across several pages, so a single stray repeat upstream would otherwise repeat
  // itself throughout the whole run — cheap to guard against here, once.
  function dedupeById(products) {
    var seen = {};
    return products.filter(function (p) {
      if (!p || !p.id || seen[p.id]) return false;
      seen[p.id] = true;
      return true;
    });
  }

  // Page 1 is the first thing anyone paging into the Import sees, so its order is
  // curated by hand rather than left to whatever order the catalog happens to
  // return: an Oscar4Fox hooded combat shirt in the big hero slot (v===0, the
  // first item in the list), a Ruck Sox item right after it, then a spread of
  // items picked to make a strong first impression. Everything not pinned here
  // keeps its normal catalog order and falls in behind — still on the Import,
  // just further back in the issue. Missing a handle (product renamed, deleted,
  // out of the current search/filter) just drops that slot rather than breaking
  // the order, so this never needs to be load-bearing.
  var PAGE1_PINNED_HANDLES = [
    'dnc-hooded-combat-shirt',                   // 1. Oscar4Fox — the hero spot
    'ruck-sox-2-0-coyote-brown',                 // 2. Ruck Sox
    'bighorn™-high-cut-ballistic-helmet-iiia-coyote-tan',
    'authentic-usgi-molle-ii-assault-pack-multicam-ocp-genuine-military-surplus-3-day-pack',
    'cold-weather-jacket-fleece-coyote-tan-level-3-polartec',
    'german-bundesmarine-navy-deck-jacket',
    'camelbak-thermobak-3-liter-hydration-carrier-no-bladder',
    // Was 'u-s-military-improved-duffel-bag-idb-od-green' — sold out
    // (0 on hand across every variant, confirmed live), so the $8 slot in
    // this curated spread was a dead "Sold Out" card for every visitor.
    //
    // The Import/home product pool is restricted upstream, in
    // snippets/sat-bootstrap.liquid, to products tagged "Shopify Collective"
    // — a handle for anything outside that tag (the store's own older,
    // largely-depleted direct inventory) just silently drops out of this
    // list rather than resolving, so the replacement has to come from
    // that same tagged pool. Genuine US Military Canteen Cup fits: $3,
    // 16 on hand, Shopify Collective-tagged — same cheap-impulse-add price
    // tier this slot was clearly meant for, and actually purchasable.
    'genuine-us-military-canteen-cup-1qt-usgi-heavy-duty-stainless-steel-issued-condition',
    'austrian-bundesheer-m65-field-jacket-like-new'
  ];
  function applyPage1Order(list) {
    var byHandle = {};
    list.forEach(function (p) { byHandle[p.handle] = p; });
    var pinnedSeen = {};
    var pinned = PAGE1_PINNED_HANDLES.map(function (h) { return byHandle[h]; }).filter(function (p) {
      if (!p || pinnedSeen[p.handle]) return false;
      pinnedSeen[p.handle] = true;
      return true;
    });
    var rest = list.filter(function (p) { return !pinnedSeen[p.handle]; });

    // Page 1's hero slot is deliberately an Oscar4Fox item (see the pin list above),
    // and Oscar4Fox already gets its own dedicated full-page house ad (page 2) plus a
    // pattern-room microsite behind it — the Import's hero spot is the brand's one ad
    // placement in the curated magazine. With 13 Oscar4Fox colorways in the real
    // catalog, leaving the rest in the pool at all meant the same brand kept turning
    // up again as a regular grid ad on a later page — first as a stray single repeat,
    // then (after they'd all been pushed toward the end of the list, an earlier fix
    // that made this worse, not better) as a cluster of several on the same page.
    // Dropped from the Import's pool entirely now: every other Oscar4Fox item is
    // still real stock, still sold, still on the Manifest — just not re-advertised a
    // second time inside this one magazine.
    var heroIsOscar4Fox = pinned[0] && pinned[0].tags && pinned[0].tags.indexOf('Oscar4Fox') !== -1;
    if (heroIsOscar4Fox) {
      rest = rest.filter(function (p) { return !p.tags || p.tags.indexOf('Oscar4Fox') === -1; });
    }

    return pinned.concat(rest);
  }

  // Multi-column layout's column-span:all is a hard break across every column,
  // not a graceful partial-row span the way CSS Grid's grid-column:span was —
  // the 7-slot shape cycle below hands out two "LARGE" (full-width) shapes per
  // 7 items, which was fine for Grid but means a short 9-item page could force
  // two or three column-balance resets, each one a chance for the browser to
  // leave a column short of its neighbors (the exact "tons of blank space"
  // symptom). Capped here to at most one full-width break per page — the very
  // first card — so every other LARGE-shaped item still gets that shape's
  // styling (bigger head, bigger price) but flows as a normal one-column card
  // instead of forcing another hard break partway down the page.
  function capHeroSpan(items) {
    var demoted = items.map(function (it, idx) {
      if (idx === 0 || it.columnSpan !== 'all') return it;
      var copy = Object.assign({}, it);
      copy.columnSpan = 'none';
      // The LARGE shapes' photoMin (160-180px) was sized assuming a full-width
      // card, where auto-ratio has a wide column to work with. Demoted into a
      // narrow one-column card, the photo container's own width shrinks to
      // roughly a third of that — a square or wide photo renders far shorter
      // than that floor, and min-height then forces the container taller than
      // the photo needs, which is empty space around the photo (letterboxing
      // one level down from the grid, not inside the grid itself). Dropped to
      // match the small shapes' own floor.
      copy.photoMin = '80px';
      return copy;
    });

    // Multi-column balance fills columns in SOURCE order, greedily, toward an
    // estimated ideal column height — the residual gap it can leave at the
    // bottom of a shorter column is bounded by roughly the size of whatever
    // item happened to land last, not some fixed amount. Reordering so the
    // taller "medium" cards (adDir:'column', photo stacked above the text)
    // run first and the shorter "small" cards (adDir:'row'/'row-reverse',
    // photo beside the text) trail gives the balance algorithm its
    // finest-grained material right where it matters — topping off whichever
    // column finishes last with a small card instead of whatever size
    // happened to be next in catalog order. Doesn't touch the hero card
    // (idx 0, already forced full-width, first) or each group's own
    // relative order — only regroups by size, so the "no two adjacent ads
    // look identical" variety inside each group is unaffected.
    var hero = demoted.length ? [demoted[0]] : [];
    var rest = demoted.slice(1);
    var medium = rest.filter(function (it) { return it.adDir === 'column'; });
    var small = rest.filter(function (it) { return it.adDir !== 'column'; });
    // Finer-grained still: within the small group, the leanest shape (v===5 —
    // no checks, no coupon, no stamp, the shortest card the cycle produces)
    // goes last. That's the best-shaped filler for whatever sliver a column
    // still comes up short by after the medium/small split above — the corner
    // that was still showing a little bare page background at the bottom of
    // a page even with that split in place.
    small.sort(function (a, b) { return (a.leanShape ? 1 : 0) - (b.leanShape ? 1 : 0); });
    return hero.concat(medium, small);
  }

  // Real column balancing for the 3-column ad grids, replacing a prior
  // post-render DOM patch (assets/sat-import-column-balance-patch.js, left
  // unwired — see sections/sat-app.liquid) that measured real rendered
  // heights and physically reordered DOM nodes after the fact. That
  // approach fought sat-runtime.js's own ownership of each element's
  // style/children (it rewrites both from renderVals() on every render,
  // including re-renders this patch's key-watching couldn't predict) and,
  // even once that was worked around, its tick()-driven rebalance still
  // never reliably applied on a real page.
  //
  // This version never touches the DOM. Every item's height is ESTIMATED
  // here, in the same renderVals() pass that already builds the item, from
  // fields the item already carries (adDir, photoMin/photoMax, headSize,
  // bodySize, showChecks/showCoupon, blurb length) — no live measurement,
  // so nothing to reconcile with the framework's own re-renders. Items are
  // then bin-packed (classic LPT: tallest first, each item dropped into
  // whichever of the 3 running column totals is currently shortest) and
  // reordered in the flat array sat-runtime.js renders — reordering the
  // SOURCE order is a completely normal, declarative renderVals() output,
  // the same as every other dynamic field already flowing through this
  // pipeline (e.g. it.columnSpan itself). The resulting target column
  // height rides along as a companion *GridStyle string; CSS column-fill
  // defaults to 'balance', a browser heuristic that doesn't guarantee even
  // columns (confirmed live: 270-340px bottom gaps even after capHeroSpan's
  // own best-effort reorder) — switching to column-fill:auto with that
  // explicit height makes the grid fill column 1 to exactly that height in
  // DOM order before overflowing to column 2 then 3, deterministically
  // matching the packing computed below instead of hoping the browser
  // guesses the same way.
  function estimateHeight(it) {
    // Parses "14px", "clamp(22px,2.6vw,31px)" (takes the clamp's own max,
    // a reasonable stand-in for its typical rendered size on a ~980px
    // page), or anything else with a trailing px number.
    function px(str, fallback) {
      var all = String(str || '').match(/[\d.]+(?=px)/g);
      return all && all.length ? parseFloat(all[all.length - 1]) : fallback;
    }

    var colWidth = 300; // approx rendered width of one of the grid's 3 columns
    var headPx = px(it.headSize, 14);
    var headH = headPx * 1.08 + 9; // line-height 1.08 + the head div's own top padding

    var photoMin = px(it.photoMin, 90);
    var photoMax = px(it.photoMax, 260);
    // Most real product photos land well under their shape's ceiling; a
    // photo 25% over its floor, capped at the ceiling, is a closer average
    // guess than either bound alone.
    var photoH = Math.min(photoMax, photoMin * 1.25);

    var bodyPx = px(it.bodySize, 10.5);
    var charsPerLine = colWidth / (bodyPx * 0.55);
    var blurbLines = Math.max(1, Math.ceil((it.blurb || '').length / charsPerLine));
    var blurbH = blurbLines * bodyPx * 1.38 + 7; // + the blurb div's own margin-bottom

    var checksH = it.showChecks === 'none' ? 0 : (3 * (bodyPx + 2) + 7);
    var couponH = it.showCoupon === 'none' ? 0 : 30;
    var priceH = 26;
    var textH = blurbH + checksH + couponH + priceH;

    // adDir:'column' stacks photo above text (full height adds); 'row'/
    // 'row-reverse' sit photo and text side by side (height is whichever
    // of the two is taller).
    var bodyH = it.adDir === 'column' ? (photoH + 9 + textH) : Math.max(photoH, textH);

    return Math.round(headH + 18 /* body wrapper's own top+bottom padding */ + bodyH + 2 /* card rule */);
  }

  // Splits the hero (columnSpan:'all') out of the returned `ordered` array
  // entirely, rather than concatenating it back in at the front the way
  // capHeroSpan does — confirmed live that Chromium mislays every item AFTER
  // a column-span:all child whenever the container also carries an explicit
  // height + column-fill:auto (the exact combination this function's height
  // enables): the rest of the grid stops being split into columns at all.
  // snippets/sat-import.liquid renders `hero` in its own unconstrained block
  // above a hero-free column-count:3 box, so `ordered`/`height` here only
  // ever need to describe that clean, span-free box.
  function packColumns(items) {
    var hero = [];
    var rest = [];
    (items || []).forEach(function (it) {
      if (it.columnSpan === 'all') hero.push(it); else rest.push(it);
    });
    if (rest.length < 2) return { hero: hero, ordered: rest, height: 0 };

    var heights = rest.map(estimateHeight);
    var order = rest.map(function (_, i) { return i; })
      .sort(function (a, b) { return heights[b] - heights[a]; });
    var bins = [[], [], []];
    var totals = [0, 0, 0];
    order.forEach(function (i) {
      var min = 0;
      if (totals[1] < totals[min]) min = 1;
      if (totals[2] < totals[min]) min = 2;
      bins[min].push(i);
      totals[min] += heights[i];
    });

    var ordered = bins[0].concat(bins[1], bins[2]).map(function (i) { return rest[i]; });
    // +1px guards against a sub-pixel rounding short-fall dropping a bin's
    // last item into the next column over.
    return { hero: hero, ordered: ordered, height: Math.ceil(Math.max(totals[0], totals[1], totals[2])) + 1 };
  }

  // Disabled for now: pushed live, then found estimateHeight()'s photo-height
  // guess is badly short of real product photos (many run much taller/more
  // portrait than the photoMin*1.25 floor assumed) — in a real live test
  // this made the actual per-column content height far exceed the computed
  // `height`, and CSS multicol's column-fill:auto respects column-count as
  // a TARGET, not a cap: when content can't fit in 3 columns at the given
  // height it adds a 4th, 5th... column rather than growing the 3 it has,
  // pushing cards off the right edge of the page ("completely ruined...
  // off the page" — merchant, live). Reverting to no override (default
  // column-fill:balance, the pre-session behavior) is the safe state while
  // estimateHeight gets properly recalibrated against real measured photo
  // heights before this is re-enabled — a wrong-but-too-tall guess only
  // costs a little blank space; a wrong-but-too-short one breaks the page.
  function gridStyle(height) {
    return '';
  }

  // assets/sat-issue-plan-patch.js (loads after this file) splices
  // wire-brief story cards into out.saiMiddlePages[].items for the two
  // spreads that carry one (K-219, Vanunu) — a real content change, not
  // just a reorder, so the height computed below for those spreads goes
  // stale the moment a story is added. Exposed here so that patch can
  // recompute both the order AND the matching height from the same
  // function this file uses, instead of silently rendering a packed
  // height that no longer matches what's actually in the grid.
  window.SATImportColumnPack = { estimateHeight: estimateHeight, packColumns: packColumns, gridStyle: gridStyle };

  function extraPageCount() {
    var raw = (window.SAT_DATA && window.SAT_DATA.settings && window.SAT_DATA.settings.saiExtraPages) || 0;
    var n = parseInt(raw, 10);
    if (!isFinite(n) || n < 0) n = 0;
    if (n > MAX_EXTRA_PAGES) n = MAX_EXTRA_PAGES;
    return n;
  }

  // Oscar4Fox's hero slot is pinned first on page 1 (PAGE1_PINNED_HANDLES below) and
  // shares the exact same "which photo actually shows the garment" problem as its own
  // pattern rail — see assets/sat-photo-picker.js. Scoped to Oscar4Fox-tagged products
  // only: running a canvas scoring pass on every one of the Import's items would be
  // wasted work for listings that only ever had one photo to begin with.
  //
  // The hero slot itself (i===0) is exempted from the picker: its whole point is a
  // real chosen lifestyle shot, and the picker's job is exactly the opposite — always
  // favoring the plain seamless-backdrop studio shot over a lifestyle one. Left
  // unexempted, the picker would keep overriding right back to the flat studio photo
  // no matter which image is set as the product's own featured one.
  function bestPhoto(self, s, p, i) {
    if (i === 0) return p.photo || '';
    if (!window.SATPhotoPicker || !p.tags || p.tags.indexOf('Oscar4Fox') === -1) return p.photo || '';
    return window.SATPhotoPicker.choose(p, function () {
      self.setState({ _photoPickTick: (s._photoPickTick || 0) + 1 });
    });
  }

  function buildMappedItems(self, s, list) {
    return list.map(function (p, i) {
      var stockLabel = p.stock <= 0 ? 'Sold Out' : (p.stock > 5 ? 'In Stock' : 'Only ' + p.stock + ' On File');
      var condShort = { Excellent: 'EXC', 'Very Good': 'VG', Good: 'GD', Fair: 'FR' }[p.cond] || 'VG';
      var base = {
        id: p.id, name: p.name, cat: p.cat, price: p.price, era: p.era, cond: p.cond,
        stock: p.stock, blurb: p.blurb, detail: p.detail, photo: bestPhoto(self, s, p, i), handle: p.handle, url: p.url,
        priceLabel: '$' + p.price,
        stockLabel: stockLabel,
        soldOut: p.stock <= 0,
        addLabel: p.stock <= 0 ? 'Sold Out' : 'Add To Crate',
        addBg: p.stock <= 0 ? '#8A8272' : '#232E38',
        addCursor: p.stock <= 0 ? 'default' : 'pointer',
        soldOutShow: p.stock <= 0 ? 'flex' : 'none',
        deptNo: 'DEPT. SAT‑' + p.id,
        catNo: 'Cat. No. SAT‑' + p.id,
        slotId: 'sai-' + p.id,
        manSlotId: 'man-' + p.id,
        checks: ['Condition: ' + p.cond, stockLabel, p.era + ' issue — no reproductions'],
        stockNo: 'STOCK NO. SAT-' + p.id,
        condLine: 'COND. ' + condShort + ' / ' + (p.stock > 5 ? 'IN STOCK' : p.stock > 0 ? 'LTD QTY' : 'WRITE FOR DETAILS') + ' / ' + String(p.era).replace('s', ' ISSUE')
      };

      // 7-slot cycle: two large, two medium, three small — asymmetric like a real page
      var v = i % 7;
      var stamps = ['SURPLUS', 'MIL-SPEC', 'NEW', 'LIMITED'];
      var stamp = stamps[i % 4];
      var stampInk = (i % 3 === 0) ? '#8E2116' : '#1A1A18';
      // Every photo in the Import carries a uniform tint so the whole issue reads
      // as one consistent print run — but a heavy sepia() + saturate() boost (the
      // original values here) pushes hard toward a brown/orange cast that muddies
      // the actual photo underneath it, which is the opposite of what a period
      // print run does: real old paper is desaturated, not colored. Rebalanced to
      // mild grayscale + light sepia just enough to read as paper-toned, with
      // saturate held at or under 1 rather than boosted, so the real photo still
      // pops through instead of drowning in ink.
      var photoStyles = [
        { f: 'grayscale(.35) contrast(1.15) brightness(1.02) sepia(.22) saturate(.92)', dot: '.42', dotSize: '2px' },
        { f: 'grayscale(.28) contrast(1.05) brightness(.99) sepia(.18) saturate(.9)', dot: '.3', dotSize: '2.5px' },
        { f: 'grayscale(.38) contrast(1.2) brightness(.97) sepia(.22) saturate(.88)', dot: '.3', dotSize: '3px' },
        { f: 'grayscale(.22) contrast(1.0) brightness(1.04) sepia(.16) saturate(.95)', dot: '.24', dotSize: '2px' }
      ][i % 4];
      function jig(n, amt) { return (((i * n) % 7) / 6 - 0.5) * amt; }
      var press = {
        stamp: stamp, stampInk: stampInk, photoFilter: photoStyles.f, photoDot: photoStyles.dot, photoDotSize: photoStyles.dotSize,
        stampRot: (i % 2 ? '-4deg' : '3deg'), rule: (i % 3 === 0 ? '1.5px' : '1px'),
        adSkew: 'translate(' + jig(13, 1.6).toFixed(2) + 'px,' + jig(29, 1.2).toFixed(2) + 'px) rotate(' + jig(17, 0.5).toFixed(2) + 'deg)',
        headSkew: 'translate(' + jig(23, 1.1).toFixed(2) + 'px,' + jig(31, 0.7).toFixed(2) + 'px)',
        inkDensity: (0.82 + ((i * 11) % 9) / 26).toFixed(2),
        photoSkew: 'rotate(' + jig(19, 0.7).toFixed(2) + 'deg)',
        edgeFade: (0.66 + ((i * 7) % 8) / 22).toFixed(2)
      };

      // The grid is CSS multi-column (column-count:3), not CSS Grid — real
      // masonry, where each card just stacks to its own natural height with
      // no shared "row" for a short card to come up short against, and no
      // stretch forcing a photo taller than its own real shape. columnSpan
      // 'all' (hero shapes) breaks a card out full-width via CSS column-span,
      // the same way a real magazine's big display ad interrupts the columns;
      // 'none' keeps it flowing in whichever column has room next. photoMin
      // is a soft floor; photoMax is a generous ceiling that only stops a truly
      // extreme photo from making one card absurd, not the primary sizing
      // mechanism anymore — auto-ratio (see sat-import.liquid) is.
      var shape;
      if (v === 0) shape = { // LARGE — full width, hero photo above
        columnSpan: 'all', adDir: 'column', rowAlign: 'stretch', photoBasis: 'auto', photoMin: '160px', photoMax: '420px',
        headFont: "'Anton', sans-serif", headSize: 'clamp(22px,2.6vw,31px)', headAlign: 'left',
        headBg: 'transparent', headColor: '#141412', headPad: '9px 11px 0', headTrack: '-.01em',
        bodyAlign: 'left', bodySize: '12px', priceAlign: 'left', priceSize: '42px', priceColor: '#8E2116',
        showChecks: 'flex', showCoupon: 'none', showPostpaid: 'inline', showStamp: 'block'
      }; else if (v === 1) shape = { // small — photo left
        columnSpan: 'none', adDir: 'row', rowAlign: 'flex-start', photoBasis: '0 0 44%', photoMin: '90px', photoMax: '260px',
        headFont: "'Oswald', sans-serif", headSize: '14px', headAlign: 'left',
        headBg: 'transparent', headColor: '#141412', headPad: '8px 9px 0', headTrack: '.04em',
        bodyAlign: 'left', bodySize: '10.5px', priceAlign: 'left', priceSize: '23px', priceColor: '#141412',
        showChecks: 'none', showCoupon: 'none', showPostpaid: 'inline', showStamp: 'none'
      }; else if (v === 2) shape = { // small — reversed head bar
        columnSpan: 'none', adDir: 'row-reverse', rowAlign: 'flex-start', photoBasis: '0 0 42%', photoMin: '90px', photoMax: '260px',
        headFont: "'Oswald', sans-serif", headSize: '13px', headAlign: 'center',
        headBg: '#141412', headColor: '#EDE8D8', headPad: '5px 9px', headTrack: '.07em',
        bodyAlign: 'left', bodySize: '10.5px', priceAlign: 'left', priceSize: '23px', priceColor: '#141412',
        showChecks: 'flex', showCoupon: 'none', showPostpaid: 'none', showStamp: 'block'
      }; else if (v === 3) shape = { // medium — centered, coupon
        columnSpan: 'none', adDir: 'column', rowAlign: 'stretch', photoBasis: 'auto', photoMin: '120px', photoMax: '340px',
        headFont: "'Alfa Slab One', Georgia, serif", headSize: '17px', headAlign: 'center',
        headBg: 'transparent', headColor: '#141412', headPad: '9px 10px 0', headTrack: '0',
        bodyAlign: 'center', bodySize: '10.5px', priceAlign: 'center', priceSize: '29px', priceColor: '#141412',
        showChecks: 'none', showCoupon: 'block', showPostpaid: 'inline', showStamp: 'none'
      }; else if (v === 4) shape = { // LARGE — full width, photo right
        columnSpan: 'all', adDir: 'row-reverse', rowAlign: 'flex-start', photoBasis: '0 0 46%', photoMin: '180px', photoMax: '420px',
        headFont: "'Anton', sans-serif", headSize: 'clamp(20px,2.3vw,27px)', headAlign: 'left',
        headBg: 'transparent', headColor: '#141412', headPad: '9px 11px 0', headTrack: '-.005em',
        bodyAlign: 'left', bodySize: '11.5px', priceAlign: 'left', priceSize: '36px', priceColor: '#8E2116',
        showChecks: 'flex', showCoupon: 'block', showPostpaid: 'inline', showStamp: 'block'
      }; else if (v === 5) shape = { // small — text-forward, tiny cut
        columnSpan: 'none', adDir: 'row', rowAlign: 'flex-start', photoBasis: '0 0 38%', photoMin: '80px', photoMax: '240px',
        headFont: "'Oswald', sans-serif", headSize: '13.5px', headAlign: 'left',
        headBg: 'transparent', headColor: '#141412', headPad: '8px 9px 0', headTrack: '.05em',
        bodyAlign: 'left', bodySize: '10px', priceAlign: 'left', priceSize: '22px', priceColor: '#141412',
        showChecks: 'none', showCoupon: 'none', showPostpaid: 'none', showStamp: 'none'
      }; else shape = { // medium — display head, no photo crop
        columnSpan: 'none', adDir: 'column', rowAlign: 'stretch', photoBasis: 'auto', photoMin: '110px', photoMax: '340px',
        headFont: "'Oswald', sans-serif", headSize: '15px', headAlign: 'left',
        headBg: 'transparent', headColor: '#141412', headPad: '8px 10px 0', headTrack: '.05em',
        bodyAlign: 'left', bodySize: '10.5px', priceAlign: 'left', priceSize: '26px', priceColor: '#141412',
        showChecks: 'flex', showCoupon: 'none', showPostpaid: 'inline', showStamp: 'none'
      };

      var out = Object.assign({}, base, press, shape);
      // Flags the leanest shape (v===5 — text-forward, tiny cut, no checks/
      // coupon/stamp) so capHeroSpan can hold it back to the very end of the
      // small group — see the comment there.
      out.leanShape = (v === 5);
      out.open = function () { self.setState({ detailId: p.id }); self.noteSeen(p.id, p.name); self.siteTone('b', 0.045); };
      out.add = function () { self.addToCart(p.id, 1); };
      var wl = s.wishlist || [];
      out.wished = wl.indexOf(p.id) !== -1 ? '#F0C77A' : 'rgba(244,239,227,.55)';
      out.toggleWish = function (e) { if (e && e.stopPropagation) e.stopPropagation(); self.toggleWishlist(p.id); };
      return out;
    });
  }

  var orig = window.SATComponent.prototype.renderVals;
  window.SATComponent.prototype.renderVals = function () {
    var out = orig.call(this);
    var self = this;
    var s = this.state;

    // Every field this patch sets below (storeItems included) only means
    // anything inside snippets/sat-import.liquid, which is only visible when
    // showStore is 'block' — i.e. on 'home' (the teaser) or 'store' (the
    // Import itself). storeItems is also the exact field name
    // snippets/sat-manifest.liquid binds its own, unrelated full-catalog grid
    // to — on any other page (manifest, archives, casefiles, routes) this
    // patch must leave it completely alone, or it silently clobbers the
    // Manifest with a 9-item slice of Import-shaped data. Real bug, found
    // after the merchant reported the Manifest breaking.
    if (s.page !== 'home' && s.page !== 'store') { return out; }

    // assets/sat-issue-plan-patch.js (loads after this file) wraps
    // visibleProducts() to return a fixed, hand-curated monthly issue in
    // page-sized blocks — Page 1, then one block per extra spread, then the
    // closing page, each sized to that page's own `size`, and already in
    // the exact order each page wants its items in — whenever a curated
    // issue is active. Checked before applyPage1Order runs, not after:
    // applyPage1Order's PAGE1_PINNED_HANDLES pin list searches the WHOLE
    // pool by handle and yanks any match to the front, regardless of which
    // page that item was actually curated for — against a curated pool
    // that's already carefully ordered and page-sized, that silently tore
    // an item out of, say, the Vanunu spread and put it in the page-1 hero
    // slot instead, then shifted every item after it by one position,
    // throwing off the exact per-page slice boundaries below. Found live:
    // the rendered page-1 hero was a Vanunu-curated pack, not the Oscar4Fox
    // hero the issue plan actually specified. Skipped entirely when a
    // curated issue is active — the issue plan is already the pinning.
    var issuePages = (window.SAT_ISSUE && Array.isArray(window.SAT_ISSUE.pages) && window.SAT_ISSUE.pages.length >= 3)
      ? window.SAT_ISSUE.pages : null;

    // Same pool, same curated order, same shapes as page 1 of the real
    // issue — see the top-of-file note on why this used to be a separate,
    // narrower pipeline for home and isn't any more.
    var pool = dedupeById(issuePages ? self.visibleProducts() : applyPage1Order(self.visibleProducts()));
    var mapped = buildMappedItems(self, s, pool);

    // Reading each page's own size here instead of the flat
    // PAGE1_ITEMS/PAGE2_ITEMS constants keeps this file's slicing in
    // lockstep with the curated issue, so a page's items always match the
    // story printed on it (a spread whose main feature is Hasenfus gets the
    // items curated for Hasenfus's spread, not an arbitrary slice of the
    // pool). Falls back to the flat constants whenever no curated issue is
    // active (a month shipped without one, or a shopper's search/sort has
    // swapped the pool back to the plain catalog — window.SAT_ISSUE still
    // exists then, so this only guards presence, not which pool is live; a
    // mid-size mismatch there is harmless, just not curated).
    var page1Size = issuePages ? (issuePages[0].size || PAGE1_ITEMS) : PAGE1_ITEMS;

    // Page 1's own sidebar (Find It Fast, Departments, Ordering Info,
    // Collective Spotlight, Reader Mail, Dealers, Free Catalog, Latest
    // Story — snippets/sat-import.liquid) is fixed lore/utility content, not
    // something that grows with the catalog the way the K-219/Vanunu wire-
    // story sidebars do. `gridSize` (the curated issue's own field,
    // defaulting to `size` — i.e. no filler — when absent) marks where the
    // real ad grid stops and the filler reserve begins, the same split
    // extraOverflow already makes for the other two sidebars below.
    var page1GridSize = issuePages ? Math.min(issuePages[0].gridSize || page1Size, page1Size) : PAGE1_ITEMS;

    var storeItemsPacked = packColumns(capHeroSpan(mapped.slice(0, page1GridSize)));
    out.storeItemsHero = storeItemsPacked.hero;
    out.storeItems = storeItemsPacked.ordered;
    out.storeItemsGridStyle = gridStyle(storeItemsPacked.height);
    out.page1SidebarFiller = mapped.slice(page1GridSize, page1Size);

    // The three base-renderVals bindings (sat-app.js) that used to switch
    // the layout to home's own abbreviated shape — sidebar hidden, single
    // column, a "featured selection" banner in place of the real page. Set
    // here to the exact same values the 'store' page already gets, so the
    // home teaser renders as literally the same page, not a look-alike.
    if (s.page === 'home') {
      out.saiSidebarShow = 'flex';
      out.saiBodyCols = '196px minmax(0,1fr)';
      out.saiFeaturedShow = 'none';
    }

    // Two real navigation bugs, reported live after the Mammoth/Mediterranean
    // work above:
    //
    // 1. "Next page from the front cover doesn't change the page." sat-app.js's
    //    own saiNavShow is `s.page==='store' ? 'flex' : 'none'` — it only checks
    //    which PAGE you're on, not whether the issue has actually been opened
    //    (s.saiOpen). That put the Prev/Next Page strip visibly on screen
    //    ABOVE the still-closed cover: clicking Next Page there runs
    //    turnInteriorNext(), which only ever touches saiInteriorPage — it
    //    never sets saiOpen, so the cover never goes anywhere and nothing
    //    visibly happens. Worse, it leaves saiInteriorPage silently bumped,
    //    so the page you land on once you DO click "Open The Issue" is off
    //    by however many times Next Page was clicked on the closed cover.
    //    Fixed by also requiring saiOpen — the nav strip (and the page
    //    counter it drives) now only exists once the magazine is actually
    //    open, matching what a merchant or shopper would expect it to mean.
    out.saiNavShow = (s.page === 'store' && s.saiOpen) ? 'flex' : 'none';

    // 2. "The main page import doesn't actually have a way to enter the
    //    import directly." saiFeaturedShow is forced 'none' on home (right
    //    above) so the "FEATURED SELECTION / SEE PP. 41-48" ribbon — which
    //    only makes sense inside the real paginated Import — doesn't show on
    //    the home teaser. But snippets/sat-import.liquid's ONLY click-through
    //    into the full Import ("Send For The Complete Book" / "Open The
    //    Import →", calling goStore) sits inside that exact same
    //    saiFeaturedShow-gated block, so hiding one hid the other: a visitor
    //    who scrolled down to the Import teaser on the home page had no way
    //    to continue into the real catalog short of scrolling back up to the
    //    header nav. Given its own binding, decoupled from saiFeaturedShow —
    //    sat-import.liquid's CTA banner switches to reading this instead of
    //    saiFeaturedShow (see that file's own comment at the same spot).
    //
    //    First attempt at this fix set it unconditionally 'flex', which
    //    solved the home teaser but also put the banner on the REAL page 1
    //    inside the actual open Import — where a shopper who is already
    //    looking at the paginated book doesn't need a button telling them to
    //    open it. Reported live as a banner that "shouldn't even be there."
    //    Scoped to the home page only, the one place this click-through was
    //    ever needed.
    out.saiImportCtaShow = (s.page === 'home') ? 'flex' : 'none';

    // Merchant: "a horrible sound effect that blasts your ears" clicking
    // through to the Import from the home page. goStore (sat-app.js) plays
    // this.siteTone('d', 0.05) — the same soft marimba tone used all over the
    // site's own UI, at a volume in line with everything else there — right
    // alongside the page change. Whatever's actually landing hard for this
    // one action specifically, goStore is shared by the header nav's own
    // "The Import" link too, so replacing it here (rather than only handling
    // it in sat-import.liquid's CTA banner) mutes it consistently from
    // every entry point into the Import, not just the home teaser.
    out.goStore = function (e) {
      if (e && e.preventDefault) e.preventDefault();
      self.setState({ page: 'store' });
      window.scrollTo(0, 0);
    };
    // Keyboard equivalent (Enter/Space on the CTA banner, see onKeyDown in
    // sat-import.liquid) — same base sound, muted the same way.
    out.goImportKey = function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        self.setState({ page: 'store' });
        window.scrollTo(0, 0);
      }
    };

    // On desktop the sidebar and the ad grid sit side by side (the 196px
    // column above), so DOM order never mattered — sidebar markup comes
    // first, grid comes second, and that's invisible in a 2-column layout.
    // Below the 900px breakpoint (sat-base.css collapses saiBodyCols'
    // grid-template-columns to a single 1fr track there) DOM order becomes
    // visual order, and sidebar-first meant a mobile visitor hit all 8
    // sidebar boxes — including the multi-paragraph Latest Story article —
    // before ever reaching a single product. Reordered with plain CSS
    // `order` (grid respects it same as flex) rather than reshuffling the
    // markup itself, only under that same breakpoint — desktop/tablet keep
    // the natural sidebar-first order either way.
    //
    // Used to be scoped to page:'home' only, on the theory that a shopper
    // opening the real Import on 'store' is "already deep in browsing" and
    // sidebar-first was fine there. Reported back as "messed up... should
    // be useable with minimal scrolling" on mobile — the real Import page
    // is exactly where this matters most, since it carries the full 8-box
    // sidebar (search, departments, ordering info, Collective spotlight,
    // reader mail, dealers, free catalog, and the multi-paragraph Latest
    // Story article) in front of every single ad on a narrow screen. Now
    // applies to both pages under the same breakpoint.
    var isNarrow = (typeof window !== 'undefined' && window.innerWidth) ? window.innerWidth <= 900 : false;
    if ((s.page === 'home' || s.page === 'store') && isNarrow) {
      out.saiAdGridOrder = '1';
      out.saiSidebarOrder = '2';
    } else {
      out.saiAdGridOrder = '0';
      out.saiSidebarOrder = '0';
    }

    // There used to be a fixed "page 3" here — the first back-of-book spread,
    // always shown right after the Oscar4Fox ad regardless of catalog size —
    // with a second, later copy of the exact same template (small ads,
    // classifieds, ordering coupon) as the guaranteed closing page. Removed as
    // its own numbered step: everything after page 1 now comes from one
    // unified pool, chunked into as many merchant-controlled extra spreads as
    // the dial and the catalog support, with a single closing back-of-book
    // page at the end — not two occurrences of the same page shape any more.
    var rest = mapped.slice(page1Size);

    // Each curated spread can carry more items than PAGE2_ITEMS — the two
    // sidebar pages (K-219, Vanunu, below) are sized PAGE2_ITEMS plus a
    // filler reserve, so the extra items ride along in the SAME chunk
    // instead of needing a second, separately-tracked leftover pool (an
    // earlier version of this file tried that; it broke the moment the
    // curated issue's page sizes were raised to close the masonry gap,
    // because a fully-sized curated issue has nothing left over — every
    // item is already spoken for by some page). spreadSizes holds each
    // extra spread's own size, in the same order the curated issue defines
    // them; closingSize is the guaranteed last page's own size. Both fall
    // back to the flat PAGE2_ITEMS constant, and extraPageCount()'s
    // merchant setting, whenever no curated issue is active.
    var spreadSizes = [];
    var closingSize = PAGE2_ITEMS;
    if (issuePages) {
      var spreadDefs = issuePages.slice(1, issuePages.length - 1);
      spreadDefs.forEach(function (def) { spreadSizes.push(def.size || PAGE2_ITEMS); });
      closingSize = issuePages[issuePages.length - 1].size || PAGE2_ITEMS;
    } else {
      // The merchant's own setting is a ceiling, not a promise there's enough
      // catalog behind it. One chunk is always reserved for the closing page
      // itself (below), so turning the dial up never produces a genuinely
      // empty extra page, and there's always at least a closing page even at
      // 0 extras.
      var totalChunks = Math.max(1, Math.ceil(rest.length / PAGE2_ITEMS));
      var extraPagesFallback = Math.min(extraPageCount(), totalChunks - 1);
      for (var sc = 0; sc < extraPagesFallback; sc++) { spreadSizes.push(PAGE2_ITEMS); }
    }
    var extraPages = spreadSizes.length;

    // The Mammoth Lakes wire report (merchant request) rides as a small section on
    // one of the existing catalog spreads rather than getting a page of its own —
    // no page-count/numbering changes needed for it at all. MAMMOTH_SPREAD_IDX picks
    // which spread (0-based into extraChunks) carries it; idx 2 is the 3rd extra
    // spread, which lands on firstMiddlePage+2 (page 5 with no partner ad) on a
    // normal-sized catalog. Clamped to whatever spreads actually exist so a smaller
    // catalog still gets the section on its own last spread instead of losing it.
    // Computed up here (rather than down with the other *_SPREAD_IDX picks, where
    // it used to live) because the page-number math right below now needs it too —
    // see PAGE_SAT_MID.
    var MAMMOTH_SPREAD_IDX = Math.min(2, spreadSizes.length - 1);

    // Page numbers are computed, not hardcoded, because the partner ad page
    // (Theme settings → The Southern Air Import → partner ad page) is optional
    // — with no image picked there it doesn't exist at all, and every page
    // after it shifts back by one. PAGE_ADGRID (1) and PAGE_OSCAR4FOX (2) never
    // move; everything from the partner ad on is relative to those.
    var partnerAdImage = window.SAT_DATA && window.SAT_DATA.settings && window.SAT_DATA.settings.saiPartnerAdImage;
    var PAGE_ADGRID = 1;
    var PAGE_OSCAR4FOX = 2;
    var PAGE_PARTNER = partnerAdImage ? 3 : 0; // 0 = doesn't exist as a page
    var firstMiddlePage = partnerAdImage ? 4 : 3; // extra spreads start right after the fixed pages

    // Merchant: move the stained-glass "War is a religion" / night-vision house
    // ad from the back of the issue to a fixed slot in the middle, directly
    // after the Mammoth Lakes spread and before the next one — breaks up the
    // run of plain catalog spreads instead of leaving all three fixed ads
    // stacked at the end. midSpreadPage(idx) is what every middle-spread page
    // number now goes through: unchanged up through MAMMOTH_SPREAD_IDX, shifted
    // up by one after it to leave room for the inserted ad page. PAGE_SAT_MID
    // is that inserted page itself, immediately after Mammoth's own page.
    function midSpreadPage(idx) {
      return firstMiddlePage + idx + (idx > MAMMOTH_SPREAD_IDX ? 1 : 0);
    }
    var PAGE_SAT_MID = firstMiddlePage + MAMMOTH_SPREAD_IDX + 1;
    // The Airwolf vintage-paperback spread takes over the stained-glass ad's
    // old end-of-issue slot (right after the last middle spread, before the
    // closing page) now that the stained-glass one has moved into the middle
    // above. Same always-present, not-merchant-configurable treatment, same
    // click-through to the full Manifest.
    var PAGE_AIRWOLF = firstMiddlePage + extraPages + 1; // +1 for the inserted mid-issue ad page
    var REPEAT_PAGE = PAGE_AIRWOLF + 1; // the single closing back-of-book page
    var TOTAL_PAGES = REPEAT_PAGE + 1; // rear cover immediately follows
    var curPage = s.saiInteriorPage || 1;

    // The October Import's five real-world wire pieces (merchant request, same
    // session as Mammoth above) ride the same way — each its own section on an
    // existing catalog spread, no new page slots, no page-count/numbering
    // changes. usedSpreadIdx reserves Mammoth's slot first (it was here
    // before these), then pickSpreadIdx hands out the rest in preference
    // order, walking down to the nearest still-free spread if the catalog is
    // ever too small to give every story its own page — sai_extra_pages is
    // currently 6, exactly enough for Mammoth plus these five with no
    // collisions, but this degrades gracefully rather than throwing if that
    // setting is ever turned down.
    // Counts per index rather than a used/unused flag: with fewer pages than
    // stories (the catalog got smaller, or a merchant turns the extra-pages
    // dial down), the old version walked straight down from `preferred` to
    // index 0 and stopped the moment it found ANY free slot below — but once
    // every index had at least one story, "free" never came, and it just
    // kept returning 0 for every story after that, piling three unrelated
    // pieces onto the front page of the run. This instead always hands out
    // whichever index currently holds the FEWEST stories (ties broken toward
    // `preferred`, then wrapping upward), so a shortfall spreads evenly
    // across whatever pages exist — at most a two-story page, never a
    // three-story one, for any six-story/four-page shortfall this issue is
    // realistically going to see.
    var usedSpreadIdx = {};
    usedSpreadIdx[MAMMOTH_SPREAD_IDX] = 1;
    function pickSpreadIdx(preferred) {
      var maxIdx = spreadSizes.length - 1;
      if (maxIdx < 0) return 0;
      var start = preferred % spreadSizes.length;
      var best = start;
      var bestCount = usedSpreadIdx[start] || 0;
      for (var k = 1; k <= maxIdx; k++) {
        var cand = (start + k) % spreadSizes.length;
        var cnt = usedSpreadIdx[cand] || 0;
        if (cnt < bestCount) { best = cand; bestCount = cnt; }
      }
      usedSpreadIdx[best] = (usedSpreadIdx[best] || 0) + 1;
      return best;
    }
    var HASENFUS_SPREAD_IDX = pickSpreadIdx(0);
    var K219_SPREAD_IDX = pickSpreadIdx(1);
    var REYKJAVIK_SPREAD_IDX = pickSpreadIdx(3);
    var VANUNU_SPREAD_IDX = pickSpreadIdx(4);
    var TAXREFORM_SPREAD_IDX = pickSpreadIdx(5);

    // Merchant: "the scaling of the story wire boxes is all messed up...
    // some can also have side sections like the Page one story box." The
    // two naturally narrow/compact shapes (the Bulletin, K-219, and the
    // Wire Brief clipping, Vanunu) get a real sidebar column on their own
    // page instead of being appended full-width below the ad grid — same
    // two-column pattern Page 1's own sidebar already uses (see
    // snippets/sat-import.liquid's data-m="sai-body"), reusing that exact
    // attribute so the existing sat-base.css rule collapsing it to one
    // column under 900px applies here for free, no new responsive CSS
    // needed. The three wide-format shapes (Hasenfus's Feature, Reykjavik's
    // 2-column Dispatch, the TaxReform banner strip) and the original
    // Mammoth piece stay full-width at the bottom of the main column —
    // none of those have a useful narrow form.
    var hasSideStoryIdx = {};
    hasSideStoryIdx[K219_SPREAD_IDX] = true;
    hasSideStoryIdx[VANUNU_SPREAD_IDX] = true;

    // Each chunk hands its first PAGE2_ITEMS items to the main ad grid on
    // the two sidebar spreads (K-219, Vanunu) — the rest overflows into
    // their own sidebar filler stack above. Every OTHER spread has no
    // sidebar to catch an overflow, so its grid gets the page's FULL
    // curated size instead of the flat PAGE2_ITEMS cap — real bug, found
    // right after the eleventh pass raised several spreads' size above 11:
    // this cap didn't know about a page's own size at all, so the extra
    // items were pulled from the pool (marked used) and then silently
    // discarded, never rendered anywhere. hasSideStoryIdx has to exist
    // before this runs, which is why this block is below it now instead
    // of above MAMMOTH_SPREAD_IDX where it used to sit.
    var extraChunks = [];
    var extraOverflow = [];
    (function () {
      var cursor = 0;
      spreadSizes.forEach(function (n, idx) {
        var chunk = rest.slice(cursor, cursor + n);
        cursor += n;
        var gridCut = hasSideStoryIdx[idx] ? PAGE2_ITEMS : n;
        extraChunks.push(packColumns(capHeroSpan(chunk.slice(0, gridCut))));
        extraOverflow.push(chunk.slice(gridCut));
      });
    })();

    // Even with the wire story riding in the 230px sidebar, one short clipping
    // next to a full multi-column ad grid left the sidebar ending a full screen
    // or more short of the grid — the "hasSideStory" fix made the two boxes'
    // SHAPES right but never touched their HEIGHTS, and a single Bulletin/Wire
    // Brief card was never going to reach as far down the page as PAGE2_ITEMS
    // ads spread across three columns next to it. Filled with a short stack of
    // compact product listings under the story, in the sidebar's own narrow
    // width — real stock, not filler graphics. Pulled from extraOverflow[idx]
    // (this same page's own chunk, past the PAGE2_ITEMS already claimed by the
    // main ad grid above), never from a separate pool — a fully-sized curated
    // issue has nothing left over anywhere else to borrow from.
    out.saiMiddlePages = extraChunks.map(function (packed, idx) {
      var pageNum = midSpreadPage(idx);
      var hasSideStory = !!hasSideStoryIdx[idx];
      return {
        items: packed.ordered,
        hero: packed.hero,
        gridStyle: gridStyle(packed.height),
        tabLabel: 'SUPPLY LOCKER ' + (idx + 2),
        show: s.page === 'home' ? 'none' : ((s.printIssue || (s.saiInteriorPage || 1) === pageNum) ? 'block' : 'none'),
        showMammoth: idx === MAMMOTH_SPREAD_IDX,
        showHasenfus: idx === HASENFUS_SPREAD_IDX,
        showK219: idx === K219_SPREAD_IDX,
        showReykjavik: idx === REYKJAVIK_SPREAD_IDX,
        showVanunu: idx === VANUNU_SPREAD_IDX,
        showTaxReform: idx === TAXREFORM_SPREAD_IDX,
        hasSideStory: hasSideStory,
        midBodyCols: hasSideStory ? '230px minmax(0,1fr)' : 'minmax(0,1fr)',
        sidebarFiller: hasSideStory ? (extraOverflow[idx] || []) : []
      };
    });
    out.saiLastTabLabel = 'SUPPLY LOCKER';

    // Merchant: real late-'80s Soldier of Fortune issues carry a genuine
    // Advertiser's Index near the back — alphabetical advertiser name + page
    // number, real content that's inherently exactly as long as the issue's
    // own real page count, so it can never itself run short or long the way
    // a fixed-template grid can. Built straight from the page numbers already
    // computed above (not a separate hand-maintained list), so it can never
    // drift out of sync with the real page order — rendered in
    // snippets/sat-import.liquid's Page 2 back matter.
    function idxLabel(idx) {
      if (idx === MAMMOTH_SPREAD_IDX) return 'Mammoth Lakes Feature';
      if (idx === HASENFUS_SPREAD_IDX) return 'Southern Air Transport';
      if (idx === K219_SPREAD_IDX) return 'K-219 Bulletin';
      if (idx === REYKJAVIK_SPREAD_IDX) return 'Reykjavik Dispatch';
      if (idx === VANUNU_SPREAD_IDX) return 'Vanunu Wire Brief';
      if (idx === TAXREFORM_SPREAD_IDX) return 'Washington Wire';
      return 'Dept. 9 Supply Locker';
    }
    // Split at MAMMOTH_SPREAD_IDX so the relocated night-vision ad's index entry
    // can be spliced in at the same point it now actually sits in the issue —
    // see midSpreadPage()/PAGE_SAT_MID above.
    var midIndexBefore = [];
    var midIndexAfter = [];
    extraChunks.forEach(function (items, idx) {
      var entry = { name: idxLabel(idx), page: midSpreadPage(idx) };
      if (idx <= MAMMOTH_SPREAD_IDX) { midIndexBefore.push(entry); } else { midIndexAfter.push(entry); }
    });
    out.saiIssueIndex = [
      { name: 'The Ad Grid — Featured Selection', page: PAGE_ADGRID },
      { name: 'Oscar4Fox — Hooded Combat Shirts', page: PAGE_OSCAR4FOX }
    ]
      .concat(partnerAdImage ? [{ name: (window.SAT_DATA && window.SAT_DATA.settings && window.SAT_DATA.settings.saiPartnerAdLabel) || 'Partner Advertisement', page: PAGE_PARTNER }] : [])
      .concat(midIndexBefore)
      .concat([{ name: 'Night Vision Devices — House Ad', page: PAGE_SAT_MID }])
      .concat(midIndexAfter)
      .concat([
        { name: 'Airwolf — Vintage Paperback Ad', page: PAGE_AIRWOLF },
        { name: 'Closing Catalog Page', page: REPEAT_PAGE }
      ]);

    // The one guaranteed closing page — same template as the extra spreads
    // (small ads, classifieds, ordering coupon) — pulls whatever's left after
    // page 1 and the extra spreads, using each spread's own (possibly
    // oversized) size rather than a flat PAGE2_ITEMS stride, so it starts
    // exactly where the curated issue's spreads actually end. Falls back to
    // page 1's own items only in the pathological case of a catalog too
    // small to fill even one closing page on its own, so the page is never
    // genuinely empty.
    var consumedByExtras = spreadSizes.reduce(function (a, b) { return a + b; }, 0);
    var closingPacked = packColumns(capHeroSpan(rest.slice(consumedByExtras, consumedByExtras + closingSize)));
    var closingItems = closingPacked.ordered;
    var closingHero = closingPacked.hero;
    var closingGridStyle = gridStyle(closingPacked.height);
    if (!closingItems.length) { closingItems = out.storeItems; closingHero = out.storeItemsHero; closingGridStyle = out.storeItemsGridStyle; }
    out.storeItemsPage2 = s.page === 'home' ? [] : closingItems;
    out.storeItemsPage2Hero = s.page === 'home' ? [] : closingHero;
    out.storeItemsPage2GridStyle = closingGridStyle;

    out.saiPage1Show = s.page === 'home' ? 'block' : ((s.printIssue || curPage === PAGE_ADGRID) ? 'block' : 'none');
    out.saiAdPageShow = s.page === 'home' ? 'none' : ((s.printIssue || curPage === PAGE_OSCAR4FOX) ? 'flex' : 'none');
    out.saiPage2Show = s.page === 'home' ? 'none' : ((s.printIssue || curPage === REPEAT_PAGE) ? 'block' : 'none');

    // The partner ad page (see snippets/sat-bootstrap.liquid, snippets/sat-import.liquid)
    // only exists when a merchant has actually picked an image for it in Theme
    // settings — PAGE_PARTNER is 0 otherwise, and curPage is never 0, so this
    // and the page-number math above both correctly skip the slot entirely.
    out.saiPartnerAdShow = s.page === 'home' ? 'none' : (partnerAdImage && (s.printIssue || curPage === PAGE_PARTNER) ? 'flex' : 'none');
    out.saiPartnerAdImage = partnerAdImage || '';
    out.saiPartnerAdUrl = (window.SAT_DATA && window.SAT_DATA.settings && window.SAT_DATA.settings.saiPartnerAdUrl) || '';
    out.saiPartnerAdLabel = (window.SAT_DATA && window.SAT_DATA.settings && window.SAT_DATA.settings.saiPartnerAdLabel) || 'Advertisement';
    out.openPartnerAd = function () {
      var url = out.saiPartnerAdUrl;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    };

    out.saiAirwolfAdShow = s.page === 'home' ? 'none' : ((s.printIssue || curPage === PAGE_AIRWOLF) ? 'flex' : 'none');
    // saiHouseAd2Show now gates the mid-issue slot (PAGE_SAT_MID) rather than
    // an end-of-issue one — kept the same output field name snippets/sat-import.liquid
    // already binds to, so no template changes were needed to move this ad.
    out.saiHouseAd2Show = s.page === 'home' ? 'none' : ((s.printIssue || curPage === PAGE_SAT_MID) ? 'flex' : 'none');
    // goManifest already exists on `out` from the base renderVals (sat-app.js) —
    // reused as-is rather than duplicated here (both fixed house ads click through to it).

    // Margin page-turn buttons, in the blank space either side of the issue
    // card. Scoped to page:'store' specifically (not the base saiPagesShow,
    // which is also 'block' for the home-page teaser embedding this same
    // page-1 markup) — the teaser isn't a real paginated read, so it gets no
    // page-turn controls. turnInteriorPrev/turnInteriorNext already handle
    // the cover/back-cover edges gracefully, so the same handlers work here.
    //
    // Merchant: "a little annoying... the ability to roll next page no
    // matter where you are on the page." These buttons already existed, but
    // snippets/sat-import.liquid positioned them position:absolute against
    // the whole #store wrapper — on a page that can run 5000-7000px tall
    // (the Import's real per-page height, confirmed live), top:50% landed
    // them once around the vertical middle of that wrapper and then left
    // them behind as soon as a reader scrolled away from that one spot,
    // instead of staying reachable. snippets/sat-import.liquid now makes
    // them position:fixed instead (same left:14px/right:14px/top:50%, now
    // against the viewport) so they stay on screen at the left/right edge
    // the whole time a page is open, the same place every time.
    //
    // Fixed-to-viewport also means they'd now paint on top of any modal
    // opened while reading (product detail, cart, wishlist) instead of
    // being buried under the page content the way the old absolute
    // positioning incidentally was — added the same overlay-open checks
    // sat-app.js's own keyboard-shortcut guard already uses (line ~152) so
    // the buttons duck out whenever one of those is open instead of
    // floating above it.
    var overlayOpen = !!(s.cartOpen || s.wishlistOpen || s.detailId || (s.view && s.view !== 'site'));
    out.saiMarginNavShow = (s.page === 'store' && s.saiOpen && !s.saiBack && !overlayOpen) ? 'block' : 'none';

    out.saiFolioLabel = (curPage === PAGE_OSCAR4FOX || (partnerAdImage && curPage === PAGE_PARTNER) || curPage === PAGE_SAT_MID || curPage === PAGE_AIRWOLF)
      ? 'Advertisement' : ('Page ' + curPage + ' of ' + TOTAL_PAGES);
    // The back cover's own "Page N of N · that's the issue" caption used to hardcode
    // "5" — correct only when saiExtraPages is 0. Bound here instead so it always
    // matches the real page count, whatever the merchant has that setting at.
    out.saiTotalPages = TOTAL_PAGES;

    out.turnInteriorNext = function () {
      self.thunk(110, 0.05, 0.045);
      var cur = s.saiInteriorPage || 1;
      if (cur >= TOTAL_PAGES - 1) { self.setState({ saiBack: true }); window.scrollTo(0, 0); return; }
      self.setState({ saiInteriorPage: cur + 1 });
      window.scrollTo(0, 0);
    };
    out.turnInteriorPrev = function () {
      self.thunk(100, 0.05, 0.04);
      var cur = s.saiInteriorPage || 1;
      if (cur <= 1) { self.setState({ saiOpen: false }); window.scrollTo(0, 0); return; }
      self.setState({ saiInteriorPage: cur - 1 });
      window.scrollTo(0, 0);
    };

    var origOpenIssue = out.openIssue;
    out.openIssue = function () {
      self.setState({ saiInteriorPage: 1 });
      if (typeof origOpenIssue === 'function') origOpenIssue();
    };
    var origCloseIssue = out.closeIssue;
    out.closeIssue = function () {
      self.setState({ saiInteriorPage: 1 });
      if (typeof origCloseIssue === 'function') origCloseIssue();
    };

    out.importTotal = String((self._products || []).length);
    out.magFolio = String(41 + self.visibleProducts().length).padStart(2, '0');

    // The retro "ORDERING INFORMATION" placard's shipping line used to quote
    // a flat "$4.70 for shipping and handling" — read as real pricing info,
    // not period flavor, sitting right next to the real order details. Real
    // freight now runs $5.99-$45+ depending on destination/weight (see
    // Settings > Shipping), so a shopper who read this literally and then
    // hit checkout got a jarring mismatch. Reworded to describe how freight
    // works instead of quoting a number that can drift out of sync again.
    if (Array.isArray(out.magOrdering)) {
      out.magOrdering = out.magOrdering.map(function (line) {
        return line === 'Add $4.70 for shipping and handling.'
          ? 'Freight is calculated by weight and destination at checkout.'
          : line;
      });
    }

    return out;
  };
})();
